import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom, useSetAtom } from 'jotai'
import './Login.scss'
import { drumtaleAdminSessionAtom, drumtaleAdminTokenAtom } from '../AtomStore'
import MainURL from '../MainURL'

const TOAST_DURATION_MS = 2400
const DEFAULT_LOGIN_ERROR_MESSAGE = '로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.'

function Login() {
  const navigate = useNavigate()
  const [isAdminSessionActive, setIsAdminSessionActive] = useAtom(drumtaleAdminSessionAtom)
  const setAdminToken = useSetAtom(drumtaleAdminTokenAtom)
  const [loginId, setLoginId] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (isAdminSessionActive) {
      navigate('/admin/dashboard')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const id = loginId.trim()
    if (!id || !loginPassword || isSubmitting) {
      return
    }

    setIsSubmitting(true)
    try {
      const response = await fetch(`${MainURL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: id, password: loginPassword }),
      })

      const data = await response.json().catch(() => null)

      if (!response.ok) {
        showToast(data?.message || DEFAULT_LOGIN_ERROR_MESSAGE)
        return
      }

      setAdminToken(data.token)
      setIsAdminSessionActive(true)
      navigate('/admin/dashboard')
    } catch (error) {
      showToast(DEFAULT_LOGIN_ERROR_MESSAGE)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePasswordRecoveryClick = () => {
    showToast('비밀번호 찾기 기능은 추후 제공됩니다.')
  }

  return (
    <div className='Login'>
      <section className='login-screen' aria-labelledby='loginTitle'>
        <form className='login-card' onSubmit={handleLoginSubmit}>
          <div className='login-brand' aria-hidden='true'>DT</div>
          <div className='login-heading'>
            <p>드럼으로 만드는 나만의 음악 이야기</p>
            <h1 id='loginTitle'>DrumTale</h1>
          </div>
          <label className='login-field'>
            아이디
            <input
              type='text'
              value={loginId}
              onChange={(event) => setLoginId(event.target.value)}
              autoComplete='username'
              required
            />
          </label>
          <label className='login-field'>
            비밀번호
            <input
              type='password'
              value={loginPassword}
              onChange={(event) => setLoginPassword(event.target.value)}
              autoComplete='current-password'
              required
            />
          </label>
          <button className='primary-button login-submit' type='submit' disabled={isSubmitting}>
            {isSubmitting ? '로그인 중...' : '로그인'}
          </button>
          <div className='login-links'>
            <button className='login-recovery' type='button' onClick={handlePasswordRecoveryClick}>비밀번호 찾기</button>
            <span className='login-links-divider' aria-hidden='true'>|</span>
            <button className='login-recovery' type='button' onClick={() => navigate('/admin/signup')}>회원가입</button>
          </div>
          <p className='login-copyright'>Copyright © JUEUNEDUTECH. All rights reserved.</p>
        </form>
      </section>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default Login
